import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AuthContextType, User, LoginRequest, LoginResponse } from '../types/auth';
import apiService from '../services/api';
import { getJwtExpSeconds } from '../utils/jwt';

/** Must show recent interaction (or returned to the tab) to extend the session. */
const ACTIVITY_WINDOW_MS = 15 * 60 * 1000;
/** Start renewing this many seconds before access token expiry. */
const REFRESH_BEFORE_EXPIRY_SEC = 10 * 60;
const SESSION_TICK_MS = 60 * 1000;

const activityEvents: (keyof WindowEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef<number>(Date.now());
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      const savedToken = localStorage.getItem("access_token");
      if (savedToken) {
        setToken(savedToken);
        try {
          const me = await apiService.getCurrentUser();
          setUser({
            username: me.username,
            display_name: me.display_name,
            email: me.email,
            is_admin: !!me.is_admin,
          });
        } catch {
          // Fallback minimal user
          setUser(null);
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const bump = () => {
      lastActivityRef.current = Date.now();
    };
    activityEvents.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const onVis = () => {
      if (document.visibilityState === 'visible') lastActivityRef.current = Date.now();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    const maybeRefresh = async () => {
      if (
        refreshInFlightRef.current ||
        document.visibilityState !== 'visible' ||
        !apiService.getToken()
      ) {
        return;
      }
      const exp = getJwtExpSeconds(token);
      if (exp == null) return;

      const nowSec = Math.floor(Date.now() / 1000);
      if (exp - nowSec > REFRESH_BEFORE_EXPIRY_SEC) return;

      if (Date.now() - lastActivityRef.current > ACTIVITY_WINDOW_MS) return;

      refreshInFlightRef.current = true;
      try {
        const next = await apiService.refreshAccessToken();
        setToken(next);
      } catch {
        /* 401 → api interceptor clears token and redirects */
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const id = window.setInterval(maybeRefresh, SESSION_TICK_MS);
    void maybeRefresh();
    return () => window.clearInterval(id);
  }, [token]);

  const login = async (credentials: LoginRequest): Promise<LoginResponse | void> => {
    try {
      const response = await apiService.login(
        credentials.username, 
        credentials.password,
        credentials.totp_code
      );
      if (response.requires_2fa) {
        return response;
      }
      const { access_token } = response;
      localStorage.setItem("access_token", access_token);
      lastActivityRef.current = Date.now();
      setToken(access_token);
      try {
        const me = await apiService.getCurrentUser();
        setUser({
          username: me.username,
          display_name: me.display_name,
          email: me.email,
          is_admin: !!me.is_admin,
        });
      } catch {
        setUser({ username: credentials.username });
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("access_token"); localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
